import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Invoice } from '../../model/invoice';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { PaymentService } from '../../service/data/payment.service';
import { IDataContext } from '../../../core/component/list/component.interface';
import { PaymentMethodService } from '../../service/data/payment-method.service';
import { LocalDate } from '../../../core/model/local-date';
import { Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { Money } from '../../../core/model/money';

@Component({
    selector: 'app-register-payment-form',
    templateUrl: './register-payment-form.component.html',
    styleUrls: ['./register-payment-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegisterPaymentFormComponent implements IDataContext, OnInit, OnDestroy {
  constructor(
    private _dataService: PaymentService,
    private _modalService: ActionWindowService,
    private _paymentMethodService: PaymentMethodService,
    cf: FormControlExFactory,
    fb: FormBuilder
  ) {
    this.form = fb.group({
      amount: cf.createMandatory('REGISTER_PAYMENT.AMOUNT', 0),
      date: cf.createMandatory('REGISTER_PAYMENT.DATE', LocalDate.today(), 'REGISTER_PAYMENT.HELP_TEXT.DATE'),
      id: cf.createHidden(''),
      companyBankAccountId: cf.create('REGISTER_PAYMENT.PAYMENT_METHOD', ''),
      verificationDescription: cf.createWithOptions('REGISTER_PAYMENT.VERIFICATION_DESCRIPTION', '', { required: true, length: 200 }),
    });
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  private _sub = new Subscription();

  private _dataContext: Invoice;
  get dataContext(): Invoice {
    return this._dataContext;
  }

  set dataContext(value: Invoice) {
    this._dataContext = value;
    this.normalizePaymentAmounts(value);
    this.form.controls['amount'].setValue(this.getSuggestedAmount(value));
    if (value.dueDate.toDate() < new Date()) {
      this.form.controls['date'].setValue(value.dueDate);
    }
    this.form.controls['id'].setValue(value.id);
    const verText = 'Bankgiro, Fakturanr ' + value.invoiceNumber + ', Avser ' + value.refersToPeriod;
    this.form.controls['verificationDescription'].setValue(verText);
  }
  readonly form: FormGroup;

  get hasUnpaidInformationAmount(): boolean {
    return this.dataContext?.unpaidInformationAmount?.amount > 0;
  }

  cancel() {
    this._modalService.close();
  }

  accountSelection: SelectionItem[];

  ngOnInit() {
    this._sub.add(
      this._dataService.getRegisterPaymentViewModel(this.dataContext.id).subscribe(model => {
        this.accountSelection = model.bankAccountSelection;
        if (this.accountSelection.length > 1) {
          this.form.controls['companyBankAccountId'].setValue(this.accountSelection[1].value);
        }
      })
    );
  }

  working: boolean;

  onSubmit() {
    if (this.form.invalid) return;

    if (this.working) return;

    this.working = true;
    let formValue = this.form.value;
    if (formValue.companyBankAccountId === '') formValue.companyBankAccountId = null;

    this._dataService.registerPayment(formValue).subscribe({
      next: res => {
        this._modalService.complete(res);
        this.working = false;
        this._modalService.close();
      },
      error: () => {
        this.working = false;
      },
    });
  }

  private getSuggestedAmount(invoice: Invoice): Money {
    const invoiceBalance = invoice.remainingAmount;
    const unpaidInformationAmount = invoice.unpaidInformationAmount;

    return new Money(invoiceBalance.currencyCode, invoiceBalance.amount + Math.max(unpaidInformationAmount.amount, 0));
  }

  private normalizePaymentAmounts(invoice: Invoice): void {
    const invoiceBalance = this.toMoney(invoice.remainingAmount);
    invoice.remainingAmount = invoiceBalance;
    invoice.unpaidInformationAmount = this.toMoney(invoice.unpaidInformationAmount, invoiceBalance.currencyCode);
  }

  private toMoney(value: Money, fallbackCurrencyCode?: string): Money {
    if (value) {
      return new Money(value.currencyCode || fallbackCurrencyCode || '', value.amount || 0);
    }

    return new Money(fallbackCurrencyCode || '', 0);
  }
}
